import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Landmark, Users, Calendar, Heart, Play } from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import FormerPresidentsSection from "@/components/public/FormerPresidentsSection";
import { Spinner } from "@/components/common/Primitives";
import { useAbout, useMemberCount } from "@/hooks/useSiteQueries";
import { withMemberCountInStats, withMemberCountInText } from "@/lib/memberCountDisplay";

const BCS_ANTHEM_VIDEO_URL = "https://www.youtube.com/watch?v=3lRcVCz0wg8";
const BCS_ANTHEM_THUMBNAIL = "https://img.youtube.com/vi/3lRcVCz0wg8/hqdefault.jpg";

export default function AboutPage() {
  const { data: about, isPending } = useAbout();
  const { data: memberCount } = useMemberCount();

  if (isPending) return <Spinner className="py-32" />;

  const {
    hero,
    vision,
    founded,
    milestones,
    programmes,
    gratitude,
    cta,
    leadership,
    milestones_section,
    programmes_section,
    leadership_section,
    former_presidents,
    former_presidents_section,
  } = about;

  return (
    <div data-testid="about-page">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="bg-maroon text-white">
        <div className="container-bcs py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow !text-gold-light mb-4">{hero.eyebrow}</p>
              <h1 className="heading-xl !text-white max-w-3xl">{hero.title}</h1>
              <p className="mt-3 text-gold-light font-serif text-xl sm:text-2xl">{hero.subtitle}</p>
              <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">{hero.intro}</p>
              {(hero.stats || []).length > 0 && (
                <dl className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-lg">
                  {withMemberCountInStats(hero.stats, memberCount).map((s) => (
                    <div key={s.label}>
                      <dt className="text-[11px] uppercase tracking-wider text-white/60">{s.label}</dt>
                      <dd className="mt-1 font-serif text-2xl sm:text-3xl font-semibold text-white">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
            <div className="lg:col-span-5">
              <a
                href={BCS_ANTHEM_VIDEO_URL}
                target="_blank"
                rel="noreferrer"
                className="group relative block rounded-2xl overflow-hidden shadow-card-hover border border-white/10"
                data-testid="about-anthem-video-btn"
                aria-label="Watch the BCS Sabha Anthem on YouTube"
              >
                <img
                  src={BCS_ANTHEM_THUMBNAIL}
                  alt="BCS Sabha Anthem"
                  className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-maroon/90 via-maroon/40 to-maroon/20 group-hover:via-maroon/50 transition-colors" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                  <span className="h-16 w-16 rounded-full bg-gold text-white flex items-center justify-center shadow-lg ring-4 ring-white/20 group-hover:scale-105 transition-transform">
                    <Play size={28} className="ml-1" fill="currentColor" />
                  </span>
                  <p className="mt-4 font-serif text-xl font-semibold text-white">Sabha Anthem</p>
                  <p className="mt-1.5 text-sm text-gold-light">Watch on YouTube</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Vision ───────────────────────────────────────── */}
      <section className="container-bcs py-20 sm:py-24">
        <div className="card-bcs p-8 sm:p-10 border-maroon/10 bg-maroon-50/30">
          <span className="h-11 w-11 rounded-lg bg-maroon-50 flex items-center justify-center text-maroon">
            <Eye size={20} />
          </span>
          <p className="mt-5 eyebrow">{vision.eyebrow}</p>
          <blockquote className="mt-3 font-serif text-2xl sm:text-3xl font-semibold text-ink leading-snug">
            &ldquo;{vision.quote}&rdquo;
          </blockquote>
        </div>
      </section>

      {/* ── Founded ──────────────────────────────────────── */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-20 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <span className="h-11 w-11 rounded-lg bg-maroon-50 flex items-center justify-center text-maroon">
                <Landmark size={20} />
              </span>
              <h2 className="mt-5 heading-md">{founded.title}</h2>
              <p className="mt-4 text-ink-soft leading-relaxed">{founded.body}</p>
              <p className="mt-4 text-ink-soft leading-relaxed">
                {withMemberCountInText(founded.membership, memberCount)}
              </p>
              <p className="mt-6 text-sm text-ink-muted border-t border-cream-dark pt-6">{founded.registration}</p>
            </div>
            <div className="card-bcs p-7">
              <h3 className="font-serif text-lg font-semibold text-ink flex items-center gap-2">
                <Users size={18} className="text-maroon" />
                {founded.founder_members_heading || "Founder members"}
              </h3>
              <ul className="mt-4 space-y-2">
                {(founded.founder_members || []).map((name) => (
                  <li key={name} className="text-sm text-ink-soft flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                    {name}
                  </li>
                ))}
              </ul>
              {founded.founder_note && (
                <p className="mt-4 text-sm text-ink-muted italic">{founded.founder_note}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Milestones ───────────────────────────────────── */}
      <section className="container-bcs py-20 sm:py-24">
        <SectionHeading
          eyebrow={milestones_section?.eyebrow || "Milestones"}
          title={milestones_section?.title || "A journey of parish units, public witness and growing impact"}
        />
        <div className="mt-12 space-y-6">
          {(milestones || []).map((m) => (
            <div key={m.period} className="flex flex-col sm:flex-row gap-4 sm:gap-8" data-testid={`milestone-${m.period}`}>
              <div className="shrink-0 sm:w-28">
                <span className="inline-block font-serif text-lg font-semibold text-maroon">{m.period}</span>
              </div>
              <div className="card-bcs p-6 flex-1">
                <h3 className="font-serif text-lg font-semibold text-ink">{m.title}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{m.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Today's work ─────────────────────────────────── */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-20 sm:py-24">
          <SectionHeading
            eyebrow={programmes_section?.eyebrow || "Today's work"}
            title={programmes_section?.title || "Programmes that carry our history forward"}
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(programmes || []).map((p) => (
              <div key={p.title} className="card-bcs p-6">
                <span className="h-10 w-10 rounded-lg bg-gold-50 flex items-center justify-center text-gold-dark">
                  <Calendar size={17} />
                </span>
                <h3 className="mt-4 font-serif text-lg font-semibold text-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-ink-soft leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gratitude ────────────────────────────────────── */}
      <section className="container-bcs py-20 sm:py-24">
        <SectionHeading eyebrow={gratitude.eyebrow} title={gratitude.title || "Standing together across the Archdiocese"} />
        <div className="mt-8 max-w-3xl space-y-4 text-ink-soft leading-relaxed">
          <p>{gratitude.body}</p>
          <p>{gratitude.closing}</p>
        </div>
      </section>

      {/* ── Leadership ───────────────────────────────────── */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-20 sm:py-24">
          <SectionHeading
            eyebrow={leadership_section?.eyebrow || "Leadership"}
            title={leadership_section?.title || "Current office bearers"}
            subtitle={leadership_section?.subtitle}
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(leadership || []).map((l, i) => (
              <div key={i} className="card-bcs p-6" data-testid={`leadership-card-${i}`}>
                {l.image_url ? (
                  <img
                    src={l.image_url}
                    alt={l.name || l.role}
                    className="h-24 w-24 rounded-xl object-cover border border-gold/20 shadow-sm"
                  />
                ) : (
                  <span className="h-24 w-24 rounded-xl bg-maroon-50 flex items-center justify-center text-maroon">
                    <Heart size={28} />
                  </span>
                )}
                <p className="mt-4 text-sm font-medium text-gold-dark">{l.role}</p>
                <h3 className="mt-1 font-serif text-lg font-semibold text-ink">{l.name}</h3>
                <p className="mt-2 text-sm text-ink-muted">{l.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Former Presidents ────────────────────────────── */}
      <FormerPresidentsSection section={former_presidents_section} presidents={former_presidents} />

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="bg-maroon">
        <div className="container-bcs py-20 text-center">
          <h2 className="heading-lg !text-white max-w-2xl mx-auto">{cta.title}</h2>
          <p className="mt-4 text-gold-light font-serif text-xl">{cta.subtitle}</p>
          <p className="mt-3 text-white/80 max-w-xl mx-auto leading-relaxed">{cta.motto}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/membership" className="btn-secondary" data-testid="about-membership-btn">
              Find your membership <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white hover:text-maroon rounded-full px-6 py-3 text-sm font-medium transition-colors"
              data-testid="about-contact-btn"
            >
              Get in touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
